import {
  ListBodySystemResponseElement,
  MriConditionInBodySystemResponse,
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO
} from '../../../types/apiGeneratedTypes';
import {
  FbsReportData,
  FbsReportItem
} from '../../FullBodyScan/fullBodyScanReportUtils';
import { getStatusDisplayForItem } from '../../FullBodyScan/fbsFindingStatuses';

export interface FlatFbsFinding {
  condition: string;
  status?: string;
  description?: string | null;
  detailedNote?: string | null;
  systemName?: string;
  organName?: string;
  item: FbsReportItem;
}

export interface MriConditionWithFinding {
  condition: MriConditionInBodySystemResponse;
  finding: FlatFbsFinding | null;
}

export interface MatchedMriCondition extends MriConditionWithFinding {
  finding: FlatFbsFinding;
}

const NO_FINDING_LABELS = new Set([
  'no findings',
  'no significant abnormality',
  'no_significant_abnormality',
  'not reported',
  'not_reported'
]);

export function flattenFbsFindings(reportData: FbsReportData): FlatFbsFinding[] {
  const findings: FlatFbsFinding[] = [];

  Object.entries(reportData).forEach(([, system]) => {
    Object.entries(system?.organs || {}).forEach(([organKey, organ]) => {
      (organ?.items || []).forEach((item) => {
        const condition = item?.condition?.trim();
        if (!condition) return;

        findings.push({
          condition,
          status: item.status,
          description: item.description,
          detailedNote: item.detailedNote ?? item.detailed_note ?? null,
          systemName: system?.name,
          organName: organ?.name || organKey,
          item
        });
      });
    });
  });

  return findings;
}

export function findFbsFinding(
  findings: FlatFbsFinding[],
  conditionName: string,
  conditionId?: number
): FlatFbsFinding | null {
  if (conditionId != null) {
    const byId = findings.find((finding) => finding.item.id === conditionId);
    if (byId) return byId;
  }

  const trimmedTarget = conditionName.trim();
  if (!trimmedTarget) return null;

  return findings.find((finding) => finding.condition.trim() === trimmedTarget) ?? null;
}

export function getBodySystemsWithContent(
  bodySystems: ListBodySystemResponseElement[]
): ListBodySystemResponseElement[] {
  return bodySystems.filter(
    (bodySystem) =>
      (bodySystem.biomarkers?.length ?? 0) > 0 || (bodySystem.mri_conditions?.length ?? 0) > 0
  );
}

export function getBiomarkersForBodySystem(
  biomarkerResults: AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO[],
  bodySystemId: number
): AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO[] {
  return biomarkerResults.filter((entry) =>
    entry.biomarker.body_systems?.some((bodySystem) => bodySystem.id === bodySystemId)
  );
}

export function getMriConditionsWithFindings(
  mriConditions: MriConditionInBodySystemResponse[],
  findings: FlatFbsFinding[]
): MriConditionWithFinding[] {
  return mriConditions.map((condition) => ({
    condition,
    finding: findFbsFinding(findings, condition.condition_description, condition.id)
  }));
}

// /** MRI conditions linked to this body system that also exist in the member scan report. */
export function getMatchedMriConditionsForBodySystem(
  mriConditions: MriConditionInBodySystemResponse[],
  findings: FlatFbsFinding[]
): MatchedMriCondition[] {
  return getMriConditionsWithFindings(mriConditions, findings).filter(
    (entry): entry is MatchedMriCondition => entry.finding !== null
  );
}

function isNoFindingLabel(label: string): boolean {
  return NO_FINDING_LABELS.has(label.trim().toLowerCase());
}

export function getBodySystemTabStatusLabel(
  mriConditions: MriConditionInBodySystemResponse[],
  findings: FlatFbsFinding[],
  usesFbsFindingStatusV2: boolean
): string {
  if (mriConditions.length === 0) {
    return 'No scan conditions';
  }

  const matchedFindings = mriConditions
    .map((condition) =>
      findFbsFinding(findings, condition.condition_description, condition.id)
    )
    .filter((finding): finding is FlatFbsFinding => finding !== null);

  if (matchedFindings.length === 0) {
    return 'No scan results';
  }

  const significantFinding = matchedFindings.find((finding) => {
    const statusDisplay = getStatusDisplayForItem(finding.item, usesFbsFindingStatusV2);
    return !isNoFindingLabel(statusDisplay.label);
  });

  if (significantFinding) {
    return getStatusDisplayForItem(significantFinding.item, usesFbsFindingStatusV2).label;
  }

  return 'No findings';
}
